import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

export const LOGO_SRC = "/brand/757-realty-logo.png";

/** Official 757 Realty logo; on dark/overlay headers it sits on a white tab so the navy and red stay legible. */
export const Wordmark = ({ light = false, className = "", showTeam = true }) => (
  <span className={`inline-flex items-center gap-3 leading-none ${className}`} data-testid="brand-wordmark">
    <span className={`inline-flex shrink-0 ${light ? "rounded-md bg-white px-2 py-1 shadow-[0_6px_18px_-8px_rgba(0,0,0,0.6)]" : ""}`}>
      <img src={LOGO_SRC} alt="757 Realty" className="h-8 w-auto sm:h-9" data-testid="brand-logo" />
    </span>
    {showTeam && (
      <span className={`team-signature hidden whitespace-nowrap font-display text-[13px] font-medium leading-none sm:block ${light ? "text-white" : "text-neutral-700"}`} data-testid="brand-team-name">
        The Rob Resh Team
      </span>
    )}
  </span>
);

const pill =
  "group inline-flex items-center justify-center gap-2 rounded-full px-6 h-11 text-[15px] font-medium transition-[background-color,color,transform] duration-200 active:scale-[0.98]";
const tones = {
  dark: "bg-ink text-white hover:bg-neutral-800",
  light: "bg-white text-ink hover:bg-neutral-100",
  outline: "border border-ink text-ink hover:bg-ink hover:text-white",
  ghostLight: "border border-white/70 text-white hover:bg-white hover:text-ink",
};

/** Compass-style pill CTA with a sliding arrow. Renders <Link>, <a> or <button>. */
export const PillButton = ({ to, href, tone = "dark", arrow = true, children, className = "", testId, ...rest }) => {
  const cls = `${pill} ${tones[tone]} ${className}`;
  const inner = (
    <>
      {children}
      {arrow && <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />}
    </>
  );
  if (to) return <Link to={to} className={cls} data-testid={testId} {...rest}>{inner}</Link>;
  if (href) return <a href={href} className={cls} data-testid={testId} {...rest}>{inner}</a>;
  return <button className={cls} data-testid={testId} {...rest}>{inner}</button>;
};

export const SectionHeading = ({ eyebrow, title, subtitle, className = "", light = false }) => (
  <div className={className}>
    {eyebrow && <p className={`eyebrow mb-3 ${light ? "!text-brass" : ""}`}>{eyebrow}</p>}
    <h2 className={`text-3xl font-normal sm:text-4xl ${light ? "text-white" : "text-ink"}`}>{title}</h2>
    {subtitle && <p className={`mt-2 max-w-2xl text-base ${light ? "text-white/75" : "text-neutral-600"}`}>{subtitle}</p>}
  </div>
);

/** Image that falls back to a neutral initials tile if the source is unavailable. */
export const SmartImg = ({ src, alt, className = "", fallbackLabel, fallbackSrc, testId }) => {
  const [failed, setFailed] = useState(!src);
  if (failed && fallbackSrc) return <img src={fallbackSrc} alt={alt} className={className} loading="lazy" data-testid={testId} />;
  if (failed) {
    const initials = (fallbackLabel || alt || "757").split(" ").map((w) => w[0]).slice(0, 2).join("");
    return (
      <div className={`grid place-items-center bg-neutral-900 text-white ${className}`} data-testid={testId} aria-label={alt}>
        <span className="font-display text-4xl italic text-brass">{initials}</span>
      </div>
    );
  }
  return <img src={src} alt={alt} className={className} onError={() => setFailed(true)} loading="lazy" data-testid={testId} />;
};

export const SampleBadge = ({ className = "" }) => (
  <span
    className={`inline-flex items-center rounded-full bg-ink px-2.5 py-1 font-mono text-[10px] font-medium uppercase tracking-[0.14em] text-white ${className}`}
    data-testid="sample-listing-badge"
  >
    Sample listing
  </span>
);

export const Container = ({ className = "", children }) => (
  <div className={`mx-auto w-full max-w-[1240px] px-5 sm:px-8 ${className}`}>{children}</div>
);
